import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, ButtonGroup } from 'react-bootstrap';
import { validatePhraseInputs } from '../../../services/blockchain.service';
import useModalHistory from '../../../hooks/useModalHistory';
import '../../../styles/phrase-modal.css'; // Import CSS file

const PhraseFormModal = ({
  show,
  onHide,
  initialData,
  isEditing = false,
  onSave,
  isLoading = false
}) => {
  useModalHistory(show, onHide, 'phrase-delete-modal');

  const [alias, setAlias] = useState('');
  const [phrases, setPhrases] = useState([]);
  const [phraseCount, setPhraseCount] = useState(12); // Default to 12 phrases
  const [errors, setErrors] = useState({});

  const inputRefs = useRef([]);

  // Initialize or reset form when modal opens or initialData changes
  useEffect(() => {
    if (show) {
      if (initialData) {
        setAlias(initialData.alias || '');
        setPhrases(initialData.phrases || []);
        setPhraseCount(initialData.phrases?.length || 12);
      } else {
        setAlias('');
        // Initialize phrases array with empty strings
        setPhrases(Array(12).fill(''));
        setPhraseCount(12);
      }
      setErrors({});
    }
  }, [show, initialData]);

  // Focus first input when modal opens
  useEffect(() => {
    if (show && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0].focus(), 100);
    }
  }, [show, phraseCount]);

  // Toggle between 12 and 24 phrases
  const togglePhraseCount = (count) => {
    if (count === phraseCount) return;

    const newPhrases = Array(count).fill('');

    // Copy existing phrases to preserve data
    for (let i = 0; i < Math.min(count, phrases.length); i++) {
      newPhrases[i] = phrases[i];
    }

    setPhrases(newPhrases);
    setPhraseCount(count);
  };

  // Handle phrase input change
  const handlePhraseChange = (index, value) => {
    const newPhrases = [...phrases];

    // Check if the input contains spaces (multiple words)
    if (value.includes(' ')) {
      const words = value.trim().split(/\s+/);

      // First word goes in the current field
      newPhrases[index] = words[0];

      // Distribute remaining words to subsequent fields
      for (let i = 1; i < words.length; i++) {
        const nextIndex = index + i;
        if (nextIndex < newPhrases.length) {
          newPhrases[nextIndex] = words[i];
        }
      }

      // Focus on the field after the last word
      const nextFieldIndex = index + words.length;
      if (nextFieldIndex < phraseCount && inputRefs.current[nextFieldIndex]) {
        setTimeout(() => inputRefs.current[nextFieldIndex].focus(), 0);
      }
    } else {
      newPhrases[index] = value;
    }

    setPhrases(newPhrases);
  };

  // Handle keyboard navigation
  const handleKeyDown = (index, e) => {
    const halfCount = phraseCount / 2;

    if (e.key === 'Enter' && index < phraseCount - 1) {
      e.preventDefault();
      inputRefs.current[index + 1].focus();
    } else if (e.key === 'ArrowDown' && index + halfCount < phraseCount) {
      e.preventDefault();
      inputRefs.current[index + halfCount].focus();
    } else if (e.key === 'ArrowUp' && index >= halfCount) {
      e.preventDefault();
      inputRefs.current[index - halfCount].focus();
    }
  };

  // Save form data
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    const validation = validatePhraseInputs(alias, phrases);
    if (!validation.isValid) {
      setErrors({ message: validation.error.message });
      return;
    }

    try {
      // Call the save function with form data
      await onSave({
        alias,
        phrases
      }, initialData?.originalName);
    } catch (error) {
      console.error('Error saving phrase:', error);
      setErrors({ message: error.message });
    }
  };

  // Generate input fields with proper numbering
  const renderPhraseInputs = () => {
    const leftColumnInputs = [];
    const rightColumnInputs = [];

    const halfCount = phraseCount / 2;

    // Generate inputs for both columns
    for (let i = 0; i < phraseCount; i++) {
      const input = (
        <div key={i} className="phrase-input-container">
          <span className="phrase-number">{i + 1}</span>
          <Form.Control
            id={`phrase-${i + 1}`}
            type="text"
            placeholder={`Word ${i + 1}`}
            value={phrases[i] || ''}
            onChange={(e) => handlePhraseChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            ref={el => inputRefs.current[i] = el}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            className="phrase-input"
          />
        </div>
      );

      // Add to appropriate column
      if (i < halfCount) {
        leftColumnInputs.push(input);
      } else {
        rightColumnInputs.push(input);
      }
    }

    return (
      <div className="phrase-grid-container">
        <div className="phrase-column">
          {leftColumnInputs}
        </div>
        <div className="phrase-column">
          {rightColumnInputs}
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="common-modal"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Secure Phrase' : 'Add Secure Phrase'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Phrase Alias*</Form.Label>
            <Form.Control
              type="text"
              id="phraseAlias"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="Enter a name for your seed phrase"
              required
            />
          </Form.Group>

          <div className="mb-3">
            <label className="form-label">Number of Words</label>
            <div className="d-flex">
              <ButtonGroup className="me-3">
                <Button
                  id="btn12"
                  onClick={() => togglePhraseCount(12)}
                  className={`phrase-count-btn ${phraseCount === 12 ? 'active' : ''}`}
                >
                  12
                </Button>

                <Button
                  id="btn24"
                  onClick={() => togglePhraseCount(24)}
                  className={`phrase-count-btn ${phraseCount === 24 ? 'active' : ''}`}
                >
                  24
                </Button>
              </ButtonGroup>
            </div>
          </div>

          {errors.message && (
            <div className="alert alert-danger">{errors.message}</div>
          )}

          {/* Render phrase inputs with CSS classes */}
          {renderPhraseInputs()}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} className="modal-close-btn">
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={isLoading}
          className="modal-action-btn phrase-btn-primary"
          id={isEditing ? "saveEditPhraseButton" : "savePhraseButton"}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PhraseFormModal;